import { Drawer, Stack, ActionIcon, Group, Image, Box, Select, Divider, Button } from '@mantine/core';
import { Organization, OrganizationContact } from '@medplum/fhirtypes';
import { useState, useRef } from 'react';
import { IconX } from '@tabler/icons-react';
import { PartnerTypePill } from './PartnerTypePill';
import { OrganizationType } from '@bariendo/lib/constants';
import { ORGANIZATION_TYPE_CODING_MAP } from '@bariendo/lib/codings';
import { PartnerAboutSection } from './PartnerAboutSection';
import { PartnerContactsSection } from './PartnerContactsSection';
import { useMedplum } from '@medplum/react';
import { alert } from '@/services/alert';
import { EditableField } from '@/components/EditableField';

function getOrganizationType(org?: Organization): OrganizationType {
  return (org?.type?.[0]?.coding?.[0]?.code as OrganizationType) || 'other';
}

function validatePartnerForm(org: Organization) {
  if (!org.name) {
    alert.error('Add a name before saving.');
    return false;
  }
  if (!org.type || org.type.length === 0) {
    alert.error('Choose a partner type.');
    return false;
  }
  return true;
}

export function PartnerCreateDrawer({
  opened,
  onClose,
  onSuccess,
}: {
  opened: boolean;
  onClose: () => void;
  onSuccess?: (organization: Organization) => void;
}) {
  const medplum = useMedplum();
  const [organization, setOrganization] = useState<Organization>({
    resourceType: 'Organization',
    name: '',
    type: [],
  });
  const [draftContacts, setDraftContacts] = useState<OrganizationContact[]>([]);
  const [pickingOrgType, setPickingOrgType] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const orgTypeSelectRef = useRef<HTMLInputElement>(null);

  const handleTypeChange = (type: OrganizationType) => {
    setOrganization({
      ...organization,
      type: [{ coding: [ORGANIZATION_TYPE_CODING_MAP[type]] }],
    });
  };

  const handleSave = async () => {
    if (!validatePartnerForm(organization)) {
      return;
    }

    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);
      const createdOrg = await medplum.createResource<Organization>({
        ...organization,
        contact: [...(organization.contact || []), ...draftContacts],
      });
      // Call onSuccess before onClose to ensure navigation happens
      if (createdOrg.id) {
        onSuccess?.(createdOrg);
      }
      onClose();
    } catch (error) {
      alert.error(`Couldn't create partner: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={() => {
        setOrganization({ resourceType: 'Organization', name: '', type: [] });
        setDraftContacts([]);
        onClose();
      }}
      position="right"
      size="xs"
      padding="md"
      styles={{ header: { padding: 0 } }}
      withCloseButton={false}
    >
      <Drawer.Header>
        <Group maw="90%">
          <Image src={'https://placehold.co/32x32'} w={32} h={32} radius="md" />
          <Stack gap={0} flex={1} style={{ overflow: 'hidden' }}>
            <EditableField
              value={organization.name || 'Untitled partner'}
              onSubmit={(value) => {
                setOrganization({ ...organization, name: value });
              }}
              editable={true}
              inputProps={{ size: 'xs' }}
              textProps={{ fw: 'bold' }}
            />
            {!pickingOrgType && (organization.type?.length ?? 0) > 0 ? (
              <Box onClick={() => setPickingOrgType(true)}>
                <PartnerTypePill type={getOrganizationType(organization)} />
              </Box>
            ) : (
              <Select
                size="xs"
                ref={orgTypeSelectRef}
                allowDeselect={false}
                label="Partner type"
                placeholder="Choose type"
                data={Object.values(OrganizationType).map((type) => ({
                  value: type,
                  label: ORGANIZATION_TYPE_CODING_MAP[type]?.display || type,
                }))}
                defaultValue={getOrganizationType(organization)}
                onChange={(value) => handleTypeChange(value as OrganizationType)}
                onBlur={() => setPickingOrgType(false)}
              />
            )}
          </Stack>
        </Group>
        <ActionIcon
          onClick={onClose}
          variant="subtle"
          color="gray"
          size={12}
          radius="md"
          style={{ position: 'absolute', top: '0', right: '0' }}
        >
          <IconX />
        </ActionIcon>
      </Drawer.Header>

      <Stack>
        <PartnerAboutSection organization={organization} onChange={(org) => setOrganization(org)} />
        <Divider />
        <PartnerContactsSection
          contacts={[]}
          onChange={() => {}}
          draftContacts={draftContacts}
          onDraftContactsChange={setDraftContacts}
        />
      </Stack>

      <Divider my="sm" />
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} loading={isSaving}>
          Save
        </Button>
      </Group>
    </Drawer>
  );
}
